import { useCallback, useEffect, useState } from 'react'
import deDE from 'antd/locale/de_DE'
import enGB from 'antd/locale/en_GB'
import dayjs from 'dayjs'
import 'dayjs/locale/en-gb'
// 'dayjs/locale/de' is imported once at the app entry (index.jsx).
import { doc, setDoc } from 'firebase/firestore'
import { useFirestore, useSigninCheck, useUser } from 'reactfire'
import * as Sentry from '@sentry/react'
import i18next, {
  type AppLocale,
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
} from './i18n'

/**
 * The app's English locale code is `en-DE` (German market, English language),
 * but AntD and dayjs need a real BCP-47 locale. Map `en-DE` → `en-GB`/`en-gb`
 * so dates/numbers/currency render with English wording and European (EUR,
 * DD/MM/YYYY) conventions; `de-DE` stays German.
 */
const ANTD_LOCALE: Record<AppLocale, typeof deDE> = {
  'de-DE': deDE,
  'en-DE': enGB,
}

const DAYJS_LOCALE: Record<AppLocale, string> = {
  'de-DE': 'de',
  'en-DE': 'en-gb',
}

function asAppLocale(lng: string | undefined): AppLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(lng ?? '')
    ? (lng as AppLocale)
    : DEFAULT_LOCALE
}

/** Drive the global dayjs locale from an app locale (`en-DE` → `en-gb`). */
function applyDayjsLocale(lng: string | undefined): void {
  dayjs.locale(DAYJS_LOCALE[asAppLocale(lng)])
}

/**
 * LocaleController. Returns the AntD locale for the active react-i18next
 * language and keeps the global dayjs locale in lockstep, so a single language
 * switch drives all three (react-i18next, AntD `ConfigProvider`, dayjs). Used as
 * the `locale` source for the top-level `ConfigProvider`.
 */
export function useActiveAntdLocale(): typeof deDE {
  const [lng, setLng] = useState<string>(i18next.language)

  useEffect(() => {
    // Sync to the language i18next resolved at init (may be `en-DE` from a
    // stored preference, overriding the module-level dayjs default).
    setLng(i18next.language)
    applyDayjsLocale(i18next.language)

    const onChange = (next: string) => {
      setLng(next)
      applyDayjsLocale(next)
    }
    i18next.on('languageChanged', onChange)
    return () => {
      i18next.off('languageChanged', onChange)
    }
  }, [])

  return ANTD_LOCALE[asAppLocale(lng)]
}

/**
 * Switch the active app language: persist to `localStorage` (the source the
 * i18next init reads on next load) and call react-i18next `changeLanguage`,
 * which the controller observes to update AntD + dayjs live.
 *
 * F4 will additionally sync the per-user Firestore `language` field; until then
 * `localStorage` is the single persistence point.
 */
export function setAppLanguage(locale: AppLocale): void {
  try {
    localStorage.setItem('language', locale)
  } catch {
    // Ignore storage failures (private mode, etc.); the in-memory switch still works.
  }
  void i18next.changeLanguage(locale)
}

/**
 * Whether the current session is an impersonation session (`impersonate/start`
 * signs the impersonator in AS the target user with an `impersonating` token
 * claim). Under the app's reactfire suspense mode this resolves before the
 * caller renders, so it is never a stale default. CL-2059 / CL-2062.
 */
function useIsImpersonating(): boolean {
  const { data: signInCheck } = useSigninCheck({
    requiredClaims: { impersonating: true },
  })
  return signInCheck?.hasRequiredClaims === true
}

/**
 * Returns a setter that switches the active language AND persists it to the
 * user's account (`userprofiles/{uid}.language`), so the preference follows the
 * account across devices (i18n F4 / CL-1551). Also writes `localStorage` + drives
 * react-i18next via {@link setAppLanguage}. The Firestore write is best-effort
 * and never blocks the UI switch.
 *
 * During impersonation the switch stays session-local: `impersonate/start`
 * signs the impersonator in AS the target user, so the signed-in uid is the
 * customer's uid and persisting would overwrite the customer's own language
 * preference (CL-2059). The impersonator's real preference reapplies from
 * their profile once they exit impersonation.
 */
export function useSetLanguage(): (locale: AppLocale) => void {
  const firestore = useFirestore()
  const { data: user } = useUser()
  const uid = user?.uid ?? null
  const isImpersonating = useIsImpersonating()

  return useCallback(
    (locale: AppLocale) => {
      setAppLanguage(locale)
      if (uid && !isImpersonating) {
        void setDoc(
          doc(firestore, 'userprofiles', uid),
          { language: locale, updatedAt: Date.now() },
          { merge: true }
        ).catch((error) => {
          // Non-blocking: the in-session switch already applied; report the
          // dropped persistence so it isn't silently lost (locale is not PII).
          Sentry.captureException(error, {
            tags: { area: 'locale', action: 'persist_language' },
            extra: { locale },
          })
        })
      }
    },
    [firestore, uid, isImpersonating]
  )
}

/**
 * Apply the account's stored language preference on sign-in (CL-1551 / i18n
 * F4). Only honours a non-German preference while `enable_english` is on;
 * otherwise forces German so English stays hidden until launch even if a stale
 * preference exists. The LocaleController reacts to the i18next change (AntD +
 * dayjs).
 *
 * Skipped entirely during impersonation (CL-2062): the signed-in profile is
 * the customer's, and applying their preference would flip the impersonator's
 * UI (and localStorage bootstrap value) to the customer's language on every
 * impersonation. The impersonator keeps their own language instead; the
 * header switcher still allows a session-local switch (CL-2059).
 */
export function useApplyProfileLanguage({
  profileStatus,
  language,
  englishEnabled,
}: {
  profileStatus: string
  language: string | undefined
  englishEnabled: boolean
}): void {
  const isImpersonating = useIsImpersonating()

  useEffect(() => {
    if (profileStatus !== 'success' || isImpersonating) return
    const target = englishEnabled ? asAppLocale(language) : DEFAULT_LOCALE
    if (i18next.language !== target) {
      // Use the shared setter so localStorage (the bootstrap source) stays in
      // sync and the correct language is applied on next load before hydration.
      setAppLanguage(target)
    }
  }, [englishEnabled, profileStatus, language, isImpersonating])
}
